define(function(require, exports, module) {
    'use strict';

    var _ = require('underscore');
    var BaseModel = require('common/base.model');
    var cashierConfig = require('portlets/cashier/cashier.config');
    var config = require('config');
    var utils = require('utils');
    var i18n = require('i18n');
    var bundle = require('bundle');

    var CashierMethodModel = BaseModel.extend({

        idAttribute: 'code',

        configType: '',
        accountSortField: 'lastDepositTime',

        confirmedFields: [
            'beneficialOwnerConfirmed',
            'feeConfirmed',
            'currencyConversion',
            'depositConfirmed',
            'loseBonus',
            'profitTaxConfirmed'
        ],

        initialize: function() {
            // We want to setup model silently so we manipulate attributes directly
            var data = this.attributes;

            this._attachCashierConfig(data);

            // pass payment type from collection
            if (this.collection) {
                data._paymentType = this.collection.paymentMethod;
            }

            this._setUpMultiFieldConfig(data);
            this._setUpLabelFieldConfig(data);
            this._resolveHardcodedFields(data);

            if (data.account) {
                this._sortAccounts(data);
                this._generateAccountTitles(data);
            }

            this._prepareValidationConfig(data);
        },

        /**
         * Attach config from cashier.config
         * @param {Object} data
         * @private
         */
        _attachCashierConfig: function(data) {
            var individualConfig = cashierConfig(data.code + this.configType);

            // COR-1734: generic is now any PM absent in cashier.config if not set explicitly
            data.isGeneric = !individualConfig || individualConfig.isGeneric;
            data.config = individualConfig || cashierConfig('generic' + this.configType);

            if (!data.config.validationConfig) {
                data.config.validationConfig = cashierConfig('basic' + this.configType).validationConfig;
            }

            data.config.redirect = 'redirect' in data.config
                ? data.config.redirect
                : this.get('depositType') === 'redirect' || this.get('withdrawType') === 'redirect';
            data.config.redirectType = data.config.redirectType || this.get('redirectType');
        },

        /**
         * Set-up multi-field account keys
         * @private
         */
        _setUpMultiFieldConfig: function(data) {
            var accountFields = data.accountFields;

            if (accountFields) {
                var keyFields = accountFields.field.filter(function(field) {
                    return field.isAccountKey;
                });

                if (keyFields.length) {
                    if (keyFields.length > 1) {
                        data.isMultiFieldKey = true;
                    }

                    data.keyFields = keyFields;
                } else {
                    data.isNoAccountKeys = true;
                }
            }
        },

        /**
         * Check for isLabelField in accountFields to display in the account dropdown
         * @param data
         * @private
         */
        _setUpLabelFieldConfig: function(data) {
            var accountFields = data.accountFields;

            if (accountFields) {
                var labelField = _.findWhere(accountFields.field, { isLabelField: true });

                if (labelField) {
                    data.labelField = labelField;
                }
            }
        },

        /**
         * Resolve account fields map for messed-up IMS-hardcoded methods
         * @param {Object} data
         * @private
         */
        _resolveHardcodedFields: function(data) {
            var map = data.config.accountFieldsMap;
            var accountFields = data.accountFields;

            if (accountFields && map) {
                accountFields.field.forEach(function(field) {
                    if (map[field.name]) {
                        field.name = map[field.name];
                    }
                });
            }
        },

        _prepareValidationConfig: function(data) {
            var validationConfig = data.config.validationConfig;

            data = data.code === 'VisaCheckout' ? data.account[0] : data;

            var accountFields = data.accountFields;
            var reChar = '/';
            var amount = validationConfig.amount;
            var limits, range;

            if ('amountRequired' in data) {
                amount.required = data.amountRequired
                    ? amount.required || true // may be an object
                    : false;
            }

            // for PM API
            if (accountFields) {
                accountFields.field.forEach(function(item) {
                    var validationRe = item.validation || '';
                    var opts = '';
                    var cfg = { required: Boolean(item.isRequired) };
                    var lastIdx;
                    var messageTKey = item.label.toLowerCase() === 'iban' ?
                        'playtech.mobile.system.mobile-cashier.generic.iban-validation' :
                        'playtech.mobile.system.mobile-cashier.generic.regexp-validation';

                    if (validationRe) {
                        if (validationRe[0] === reChar) {
                            lastIdx = validationRe.lastIndexOf(reChar);
                            opts = validationRe.substring(lastIdx + 1);
                            validationRe = validationRe.substring(1, lastIdx);
                        }
                        validationRe = '^$|(' + validationRe + ')';
                        cfg.regexp = {
                            regexp: new RegExp(validationRe, opts),
                            message: i18n.t(messageTKey)
                        };
                    }

                    validationConfig[item.name] = utils.deepExtend(cfg, validationConfig[item.name]);
                });
            }

            if (amount && amount.range) {
                limits = data.limits;
                range = amount.range;
                if (limits || range.min || range.max) {
                    if (typeof range !== 'object') {
                        range = amount.range = {};
                    }
                    if (limits) {
                        range.min = limits.min;
                        range.max = limits.max;
                    }

                } else {
                    // Do not use range validation without actual limits
                    delete amount.range;
                }
            }
        },

        /**
         * Sort accounts. By default - by last deposit
         * @param {Object} data
         * @private
         */
        _sortAccounts: function(data) {
            var sortingField = this.accountSortField;

            data.account.sort(function(acc1, acc2) {
                var date1 = new Date(acc1[sortingField]).getTime();
                var date2 = new Date(acc2[sortingField]).getTime();

                return utils.predicate.numberPredicate(date1, date2);
            });
        },

        /**
         * Generate account title
         * @param {Object} data
         * @private
         */
        _generateAccountTitles: function(data) {
            data.account.forEach(function(acc) {
                if (data.labelField) {
                    acc.title = acc.specificParams[data.labelField.name];
                } else if (data.keyFields) {
                    acc.title = data.keyFields.map(function(keyField) {
                        return acc.specificParams[keyField.name];
                    }).join('/');
                }
            });
        },

        getBackUrl: function(action, status, extraParams, origin) {
            var url = new URL(origin || location);
            var searchParams = url.searchParams;

            searchParams.set('type', this.get('code'));
            searchParams.set('action', action);
            searchParams.set('status', status);

            _.mapObject(extraParams, function(value, key) {
                searchParams.set(key, value);
            });

            return url.toString();
        },

        getBackUrls: function(request, action, redirectType) {
            var themeName = config.get('themeName');

            return bundle.cashier.addBackUrls({
                code: this.get('code'),
                action: action,
                themeName: themeName,
                redirectType: redirectType,
                initiator: 'cashier_backbone'
            })(request);
        },

        resolveStoredAccount: function(data) {
            var accountCode, account;

            accountCode = data.storedAccountId;
            if (accountCode === 'add' || accountCode == null) {
                // form data is good as it is:
                // it is a new account either with `account` selector present or without
                return data;
            }

            // Use accountCode for all non-generic methods (including CCs)
            if (!this.get('config').isGeneric) {
                data.accountCode = accountCode;
            }

            // For Credit Cards we don't send all account data
            if (this.get('config').creditCard) {
                return data;
            }

            account = this.findAccount(accountCode);
            if (!account) {
                // Having an accountCode without actual account is some internal error in this place
                throw new Error('FE Cashier Error: Selected account not found');
            }

            return _.extend({}, account.specificParams, data);
        },

        findAccount: function(accountCode) {
            return _.find(this.get('account'), function(acc) {
                return acc.code === accountCode;
            });
        }

    });

    module.exports = CashierMethodModel;
});
